import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const VolunteerForm = () => {
    const initialValues = {
        full_name: "",
        phone_number: "",
        email: "",
        address: "",

        availability_type: "",
        selected_days: [],
        time_slots: [],

        duration_type: "",
        start_date: "",
        end_date: "",

        work_interest: "",

        volunteer_reason: "",
        organization_interest: "",

        skills: "",
        experience: "",

        police_check: "",
        certifications: [],
        other_certifications: "",

        applicant_references: [{ name: "", phone: "", email: "" }],

        emergency_contact_name: "",
        emergency_contact_relation: "",
        emergency_contact_phone: "",

        medical_conditions: "",

        signature_name: "",
        agreement_date: "",

        agreed_to_policies: false,
    };

    const validationSchema = Yup.object({
        full_name: Yup.string().required("Name is required"),
        phone_number: Yup.string()
            .matches(/^[0-9+\-() ]+$/, "Invalid phone number")
            .required("Phone number is required"),
        email: Yup.string().email("Invalid email").required("Email is required"),
        address: Yup.string().required("Address is required"),

        availability_type: Yup.string().required("Select availability"),
        selected_days: Yup.array().when("availability_type", {
            is: (val) => val && val.length > 0,
            then: (schema) => schema.min(1, "Select at least one day"),
            otherwise: (schema) => schema.optional(),
        }),
        time_slots: Yup.array().min(1, "Select at least one time slot"),

        duration_type: Yup.string().required("Select duration"),
        start_date: Yup.string().when("duration_type", {
            is: "short_term",
            then: (schema) => schema.required("Start date is required"),
            otherwise: (schema) => schema.optional(),
        }),
        end_date: Yup.string().when("duration_type", {
            is: "short_term",
            then: (schema) => schema.required("End date is required"),
            otherwise: (schema) => schema.optional(),
        }),

        work_interest: Yup.string().required("Please state your interest"),
        volunteer_reason: Yup.string().required("Required"),
        organization_interest: Yup.string().required("Required"),

        skills: Yup.string().required("Please list your skills"),
        police_check: Yup.string().required("Required"),

        applicant_references: Yup.array().of(
            Yup.object().shape({
                name: Yup.string().required("Name is required"),
                phone: Yup.string().required("Phone is required"),
                email: Yup.string().email("Invalid email").required("Email is required"),
            })
        ).min(1, "At least one reference is required"),

        emergency_contact_name: Yup.string().required("Required"),
        emergency_contact_relation: Yup.string().required("Required"),
        emergency_contact_phone: Yup.string().required("Required"),

        signature_name: Yup.string().required("Required"),
        agreement_date: Yup.string().required("Required"),
        agreed_to_policies: Yup.boolean().oneOf([true], "You must agree to policies"),
    });

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const formData = new FormData();

            // Append all top-level fields
            Object.keys(values).forEach(key => {
                if (key === "applicant_references") {
                    // Handle nested references
                    values.applicant_references.forEach((ref, index) => {
                        formData.append(`applicant_references[${index}][name]`, ref.name);
                        formData.append(`applicant_references[${index}][phone]`, ref.phone);
                        formData.append(`applicant_references[${index}][email]`, ref.email);
                    });
                } else if (Array.isArray(values[key])) {
                    // Handle array fields (like selected_days, time_slots)
                    // If certifications, join them; otherwise send as discrete array for PHP json_encode
                    if (key === "certifications") {
                        const allCerts = [...values.certifications];
                        if (values.other_certifications) allCerts.push(values.other_certifications);
                        formData.append(key, allCerts.join(", "));
                    } else {
                        values[key].forEach(val => formData.append(`${key}[]`, val));
                    }
                } else if (typeof values[key] === "boolean") {
                    formData.append(key, values[key] ? "1" : "0");
                } else {
                    // For police_check, map to 1/0 if needed
                    if (key === "police_check") {
                        formData.append(key, values[key] === "yes" ? "1" : "0");
                    } else {
                        formData.append(key, values[key]);
                    }
                }
            });

            const response = await fetch("https://bntserver.bntfoundation.com/VolunteerApplicationAdd.php", {
                method: "POST",
                body: formData,
            });

            const result = await response.text();
            if (response.ok) {
                toast.success(result || "Form submitted successfully!");
                resetForm();
            } else {
                toast.error("Submission failed. Please try again.");
            }
        } catch (error) {
            console.error("Submission Error:", error);
            toast.error("An error occurred during submission.");
        } finally {
            setSubmitting(false);
        }
    };

    return (


        <>
            {/* HERO */}
            <div
                className="hero page-inner overlay"
                style={{ backgroundImage: `url('../../Content/img/slide/BNT5.jpg')` }}
            >
                <div className="container">
                    <div className="row justify-content-center align-items-center">
                        <div className="col-lg-9 text-center mt-5">
                            <h1 className="heading">Volunteer Engagement Form</h1>
                            <p className="text-white mt-3">Join our team and make a difference.</p>
                        </div>
                    </div>
                </div>
            </div>


            <div className="container py-5">
                <ToastContainer />

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values, setFieldValue }) => (
                        <Form className="row g-3">

                            {/* ABOUT */}
                            <h4>About You</h4>

                            <div className="col-md-6">
                                <Field name="full_name">
                                    {({ field, meta }) => (
                                        <>
                                            <input {...field} type="text" className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`} placeholder="Full Name" />
                                            <ErrorMessage name="full_name" component="div" className="invalid-feedback" />
                                        </>
                                    )}
                                </Field>
                            </div>

                            <div className="col-md-6">
                                <Field name="phone_number">
                                    {({ field, meta }) => (
                                        <>
                                            <input {...field} type="text" className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`} placeholder="Phone Number" />
                                            <ErrorMessage name="phone_number" component="div" className="invalid-feedback" />
                                        </>
                                    )}
                                </Field>
                            </div>

                            <div className="col-md-6">
                                <Field name="email">
                                    {({ field, meta }) => (
                                        <>
                                            <input {...field} type="email" className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`} placeholder="Email" />
                                            <ErrorMessage name="email" component="div" className="invalid-feedback" />
                                        </>
                                    )}
                                </Field>
                            </div>

                            <div className="col-md-6">
                                <Field name="address">
                                    {({ field, meta }) => (
                                        <>
                                            <input {...field} type="text" className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`} placeholder="City + Postal Code" />
                                            <ErrorMessage name="address" component="div" className="invalid-feedback" />
                                        </>
                                    )}
                                </Field>
                            </div>

                            {/* AVAILABILITY */}
                            <h4 className="mt-4">Availability</h4>

                            <div className="col-md-6">
                                <Field name="availability_type">
                                    {({ field, meta }) => (
                                        <>
                                            <select {...field} className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}>
                                                <option value="">Days Available</option>
                                                <option value="weekdays">Weekdays</option>
                                                <option value="weekends">Weekends</option>
                                                <option value="both">Both</option>
                                            </select>
                                            <ErrorMessage name="availability_type" component="div" className="invalid-feedback" />
                                        </>
                                    )}
                                </Field>
                            </div>

                            {values.availability_type && (
                                <div className="col-12">
                                    <label className="d-block mb-2">Available Days:</label>
                                    {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(day => (
                                        <label key={day} className="me-3">
                                            <input
                                                type="checkbox"
                                                checked={values.selected_days.includes(day)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setFieldValue("selected_days", [...values.selected_days, day]);
                                                    } else {
                                                        setFieldValue("selected_days", values.selected_days.filter(d => d !== day));
                                                    }
                                                }}
                                            /> {day}
                                        </label>
                                    ))}
                                    <ErrorMessage name="selected_days" component="div" className="text-danger" />
                                </div>
                            )}

                            <div className="col-12">
                                <label className="d-block mb-2">Available Time Slots:</label>
                                {["Morning", "Afternoon", "Evening"].map(time => (
                                    <label key={time} className="me-3">
                                        <input
                                            type="checkbox"
                                            checked={values.time_slots.includes(time)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setFieldValue("time_slots", [...values.time_slots, time]);
                                                } else {
                                                    setFieldValue("time_slots", values.time_slots.filter(t => t !== time));
                                                }
                                            }}
                                        /> {time}
                                    </label>
                                ))}
                                <ErrorMessage name="time_slots" component="div" className="text-danger" />
                            </div>

                            <div className="col-md-6">
                                <Field name="duration_type">
                                    {({ field, meta }) => (
                                        <>
                                            <select {...field} className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}>
                                                <option value="">Duration</option>
                                                <option value="short_term">Short-Term</option>
                                                <option value="ongoing">Ongoing</option>
                                            </select>
                                            <ErrorMessage name="duration_type" component="div" className="invalid-feedback" />
                                        </>
                                    )}
                                </Field>
                            </div>

                            {values.duration_type === "short_term" && (
                                <>
                                    <div className="col-md-3">
                                        <Field name="start_date">
                                            {({ field, meta }) => (
                                                <>
                                                    <input {...field} type="date" className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`} />
                                                    <ErrorMessage name="start_date" component="div" className="invalid-feedback" />
                                                </>
                                            )}
                                        </Field>
                                    </div>
                                    <div className="col-md-3">
                                        <Field name="end_date">
                                            {({ field, meta }) => (
                                                <>
                                                    <input {...field} type="date" className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`} />
                                                    <ErrorMessage name="end_date" component="div" className="invalid-feedback" />
                                                </>
                                            )}
                                        </Field>
                                    </div>
                                </>
                            )}

                            {/* INTEREST */}
                            <h4 className="mt-4">Interest Areas</h4>

                            {/* <div className="col-12">
              {values.interestAreas.map((item, index)=>(
                <div key={index} className="d-flex mb-2 gap-2">
                  <input
                    className="form-control"
                    placeholder="e.g. Youth support, Events..."
                    value={item}
                    onChange={(e)=>{
                      const updated=[...values.interestAreas];
                      updated[index]=e.target.value;
                      setFieldValue("interestAreas",updated);
                    }}
                  />
                  {values.interestAreas.length>1 && (
                    <button type="button" className="btn btn-danger"
                      onClick={()=>{
                        setFieldValue("interestAreas",
                          values.interestAreas.filter((_,i)=>i!==index));
                      }}>
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button type="button" className="btn btn-secondary"
                onClick={()=>setFieldValue("interestAreas",[...values.interestAreas,""])}>
                + Add
              </button>
            </div> */}
                            <div className="col-12">
                                <Field name="work_interest">
                                    {({ field, meta }) => (
                                        <>
                                            <textarea {...field} className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`} placeholder="e.g. Youth support, Events..." />
                                            <ErrorMessage name="work_interest" component="div" className="invalid-feedback" />
                                        </>
                                    )}
                                </Field>
                            </div>

                            {/* STORY */}
                            <div className="col-12">
                                <Field name="volunteer_reason">
                                    {({ field, meta }) => (
                                        <>
                                            <textarea {...field} className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`} placeholder="Why do you want to volunteer?" />
                                            <ErrorMessage name="volunteer_reason" component="div" className="invalid-feedback" />
                                        </>
                                    )}
                                </Field>
                            </div>

                            <div className="col-12">
                                <Field name="organization_interest">
                                    {({ field, meta }) => (
                                        <>
                                            <textarea {...field} className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`} placeholder="Why this organization?" />
                                            <ErrorMessage name="organization_interest" component="div" className="invalid-feedback" />
                                        </>
                                    )}
                                </Field>
                            </div>

                            {/* SKILLS */}
                            <div className="col-12">
                                <Field name="skills">
                                    {({ field, meta }) => (
                                        <>
                                            <input {...field} type="text" className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`} placeholder="Skills" />
                                            <ErrorMessage name="skills" component="div" className="invalid-feedback" />
                                        </>
                                    )}
                                </Field>
                            </div>

                            <div className="col-12">
                                <Field as="textarea" name="experience" className="form-control" placeholder="Previous experience (optional)" />
                            </div>

                            {/* POLICE */}
                            <div className="col-md-6">
                                <Field name="police_check">
                                    {({ field, meta }) => (
                                        <>
                                            <select {...field} className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}>
                                                <option value="">Police Check</option>
                                                <option value="yes">Yes</option>
                                                <option value="no">No</option>
                                            </select>
                                            <ErrorMessage name="police_check" component="div" className="invalid-feedback" />
                                        </>
                                    )}
                                </Field>
                            </div>

                            {/* CERTIFICATIONS */}
                            <div className="col-12">
                                {["First Aid", "CPR"].map(cert => (
                                    <label key={cert} className="me-3">
                                        <input
                                            type="checkbox"
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setFieldValue("certifications", [...values.certifications, cert]);
                                                } else {
                                                    setFieldValue("certifications",
                                                        values.certifications.filter(c => c !== cert));
                                                }
                                            }}
                                        /> {cert}
                                    </label>
                                ))}
                            </div>

                            <div className="col-12">
                                <Field name="other_certifications" className="form-control" placeholder="Other certifications" />
                            </div>

                            {/* REFERENCES */}
                            <h4 className="mt-4">References</h4>

                            {values.applicant_references.map((ref, index) => (
                                <div key={index} className="row mb-3 p-3 border rounded bg-light mx-0">
                                    <div className="col-12 d-flex justify-content-between align-items-center mb-2">
                                        <h6 className="mb-0">Reference #{index + 1}</h6>
                                        {values.applicant_references.length > 1 && (
                                            <button type="button" className="btn btn-sm btn-outline-danger"
                                                onClick={() => setFieldValue("applicant_references", values.applicant_references.filter((_, i) => i !== index))}>
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                    <div className="col-md-4">
                                        <Field name={`applicant_references[${index}].name`}>
                                            {({ field, meta }) => (
                                                <>
                                                    <input {...field} className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`} placeholder="Name" />
                                                    <ErrorMessage name={`applicant_references[${index}].name`} component="div" className="invalid-feedback" />
                                                </>
                                            )}
                                        </Field>
                                    </div>
                                    <div className="col-md-4">
                                        <Field name={`applicant_references[${index}].phone`}>
                                            {({ field, meta }) => (
                                                <>
                                                    <input {...field} className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`} placeholder="Phone" />
                                                    <ErrorMessage name={`applicant_references[${index}].phone`} component="div" className="invalid-feedback" />
                                                </>
                                            )}
                                        </Field>
                                    </div>
                                    <div className="col-md-4">
                                        <Field name={`applicant_references[${index}].email`}>
                                            {({ field, meta }) => (
                                                <>
                                                    <input {...field} className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`} placeholder="Email" />
                                                    <ErrorMessage name={`applicant_references[${index}].email`} component="div" className="invalid-feedback" />
                                                </>
                                            )}
                                        </Field>
                                    </div>
                                </div>
                            ))}

                            <div className="col-12 col-md-3">

                                <button type="button" className="btn btn-secondary w-100"
                                    onClick={() => setFieldValue("applicant_references",
                                        [...values.applicant_references, { name: "", phone: "", email: "" }])}>
                                    + Add Reference
                                </button>
                            </div>



                            {/* EMERGENCY */}
                            <h4 className="mt-4">Emergency Contact</h4>

                            <div className="col-md-4">
                                <Field name="emergency_contact_name">
                                    {({ field, meta }) => (
                                        <>
                                            <input {...field} type="text" className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`} placeholder="Name" />
                                            <ErrorMessage name="emergency_contact_name" component="div" className="invalid-feedback" />
                                        </>
                                    )}
                                </Field>
                            </div>

                            <div className="col-md-4">
                                <Field name="emergency_contact_relation">
                                    {({ field, meta }) => (
                                        <>
                                            <input {...field} type="text" className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`} placeholder="Relation" />
                                            <ErrorMessage name="emergency_contact_relation" component="div" className="invalid-feedback" />
                                        </>
                                    )}
                                </Field>
                            </div>

                            <div className="col-md-4">
                                <Field name="emergency_contact_phone">
                                    {({ field, meta }) => (
                                        <>
                                            <input {...field} type="text" className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`} placeholder="Phone" />
                                            <ErrorMessage name="emergency_contact_phone" component="div" className="invalid-feedback" />
                                        </>
                                    )}
                                </Field>
                            </div>

                            {/* MEDICAL */}
                            <div className="col-12">
                                <Field as="textarea" name="medical_conditions" className="form-control" placeholder="Medical / accessibility (optional)" />
                            </div>

                            {/* SIGNATURE */}
                            <h4 className="mt-4">Agreement</h4>

                            <div className="col-md-6">
                                <Field name="signature_name">
                                    {({ field, meta }) => (
                                        <>
                                            <input {...field} type="text" className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`} placeholder="Full Name (Signature)" />
                                            <ErrorMessage name="signature_name" component="div" className="invalid-feedback" />
                                        </>
                                    )}
                                </Field>
                            </div>

                            <div className="col-md-6">
                                <Field name="agreement_date">
                                    {({ field, meta }) => (
                                        <>
                                            <input {...field} type="date" className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`} />
                                            <ErrorMessage name="agreement_date" component="div" className="invalid-feedback" />
                                        </>
                                    )}
                                </Field>
                            </div>

                            <div className="col-12">
                                <div className="form-check">
                                    <Field name="agreed_to_policies">
                                        {({ field, meta }) => (
                                            <>
                                                <input {...field} type="checkbox" className={`form-check-input ${meta.touched && meta.error ? 'is-invalid' : ''}`} id="agreed_to_policies" />
                                                <label className="form-check-label" htmlFor="agreed_to_policies">
                                                    I agree to policies
                                                </label>
                                                <ErrorMessage name="agreed_to_policies" component="div" className="invalid-feedback" />
                                            </>
                                        )}
                                    </Field>
                                </div>
                            </div>

                            <div className="col-12">
                                <button type="submit" className="btn btn-primary">Submit</button>
                            </div>

                        </Form>
                    )}
                </Formik>
            </div>
        </>
    );
};

export default VolunteerForm;